import { useCallback, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useInfiniteClubEventsQuery } from '@features/club/api/hooks';
import {
  activeTournamentParticipantsQueryOptions,
  tournamentCardQueryOptions,
} from '@features/tournament/api/hooks';
import { roomKey } from '@features/video/lib/ticket';
import { RoomRef } from '@features/video/types';

import { PickerRoom, PickerTournament, RoomPickerModal } from './RoomPickerModal';

// Турнир попадает в выбор, если начнется не позже чем через столько
const SOON_MS = 60 * 60 * 1000;

type Props = {
  clubId: string;
  // основная комната — путь страницы, ее не убрать
  main: RoomRef;
  // комнаты, уже собранные в мультикомнату
  selected: RoomRef[];
  onApply: (rooms: RoomRef[]) => void;
  onClose: () => void;
};

// Выбор комнат: турниры клуба, которые идут или начнутся в ближайший час, из общего списка,
// плюс турниры подключенных комнат (из адреса) без оглядки на фильтры — их названия из
// карточки, если в списке их нет. Карточка (видео включено, число комнат) и участники
// (кто в какой) — у всех сразу, список показываем, когда загрузились все: без построчной
// подгрузки. Турниры без видеоконтроля и с неудавшимся запросом в список не попадают
export function RoomPickerModalWidget({ clubId, main, selected, onApply, onClose }: Props) {
  const { data, isPending } = useInfiniteClubEventsQuery({
    clubId,
    type: 'upcoming',
    isManager: true,
  });
  const connectedIds = useMemo(() => [...new Set(selected.map((r) => r.tournamentId))], [selected]);
  const ids = useMemo(() => {
    const limit = Date.now() + SOON_MS;
    const connected = new Set(connectedIds);
    const listed = (data ?? [])
      .filter(
        (t) =>
          connected.has(t.tournamentId) ||
          (t.status !== 'Finished' &&
            t.status !== 'Deleted' &&
            t.status !== 'Draft' &&
            t.startTimestampMs <= limit),
      )
      .map((t) => t.tournamentId);
    return [...listed, ...connectedIds.filter((id) => !listed.includes(id))];
  }, [data, connectedIds]);

  const cards = useQueries({
    queries: ids.map((id) => tournamentCardQueryOptions(clubId, id)),
  });
  const participants = useQueries({
    queries: ids.map((id) => ({
      ...activeTournamentParticipantsQueryOptions(clubId, id, 'AlphabetAsc', true),
      refetchOnWindowFocus: false,
    })),
  });
  const isLoading =
    isPending || cards.some((q) => q.isPending) || participants.some((q) => q.isPending);

  const tournaments: PickerTournament[] = isLoading
    ? []
    : ids
        .flatMap((id, i) => {
          const card = cards[i]?.data;
          const list = participants[i]?.data;
          if (!card?.videoSettings?.enable || !list) return [];
          const active = list
            .filter((p) => p.status === 'Participating')
            .map((p) => ({
              userId: p.userId,
              name: `${p.lastName} ${p.firstName}`.trim(),
              room: p.videoRoomNumber,
            }));
          const count = Math.max(card.videoRoomsCount ?? 0, ...active.map((p) => p.room ?? 0));
          const rooms: PickerRoom[] = Array.from({ length: count }, (_, k) => ({
            number: k + 1,
            participants: active.filter((p) => p.room === k + 1),
          }));
          return [
            {
              tournamentId: id,
              title: card.title,
              start: card.startTimestampMs ?? 0,
              rooms,
              absent: active.filter((p) => p.room === null),
            },
          ];
        })
        // турнир основной комнаты всегда сверху, остальные по времени начала
        .sort(
          (a, b) =>
            Number(b.tournamentId === main.tournamentId) -
              Number(a.tournamentId === main.tournamentId) || a.start - b.start,
        );

  const [picked, setPicked] = useState<RoomRef[]>(selected);
  const onToggle = useCallback((room: RoomRef) => {
    const key = roomKey(room);
    setPicked((prev) =>
      prev.some((r) => roomKey(r) === key)
        ? prev.filter((r) => roomKey(r) !== key)
        : [...prev, room],
    );
  }, []);

  return (
    <RoomPickerModal
      tournaments={tournaments}
      isLoading={isLoading}
      main={main}
      selected={picked}
      onToggle={onToggle}
      onApply={() => onApply(picked)}
      onClose={onClose}
    />
  );
}
